import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Underline from '@tiptap/extension-underline';
import TextAlign from '@tiptap/extension-text-align';
import { TextStyle } from '@tiptap/extension-text-style';
import { Color } from '@tiptap/extension-color';
import Link from '@tiptap/extension-link';
import { 
  Bold, 
  Italic, 
  Underline as UnderlineIcon, 
  AlignLeft, 
  AlignCenter, 
  AlignRight,
  List,
  ListOrdered,
  Link as LinkIcon,
  Palette
} from 'lucide-react';

const MenuBar = ({ editor }) => {
  if (!editor) return null;

  const colors = [
    { name: 'Gold', value: '#D4AF37' },
    { name: 'White', value: '#F5F5F0' },
    { name: 'Gray', value: '#A1A1AA' },
    { name: 'Black', value: '#050505' },
    { name: 'Red', value: '#EF4444' },
    { name: 'Green', value: '#22C55E' },
    { name: 'Blue', value: '#3B82F6' },
  ];

  const setLink = () => {
    const url = window.prompt('Enter URL:');
    if (url) {
      editor.chain().focus().setLink({ href: url }).run();
    }
  };

  return (
    <div className="flex flex-wrap gap-1 p-2 border-b border-[rgba(212,175,55,0.2)] bg-[#0a0a0a]">
      {/* Text Formatting */}
      <button
        type="button"
        onClick={() => editor.chain().focus().toggleBold().run()}
        className={`p-2 hover:bg-[rgba(212,175,55,0.2)] transition-colors ${
          editor.isActive('bold') ? 'bg-[rgba(212,175,55,0.3)] text-[#D4AF37]' : 'text-[#A1A1AA]'
        }`}
        title="Bold"
      >
        <Bold className="w-4 h-4" />
      </button>
      <button
        type="button"
        onClick={() => editor.chain().focus().toggleItalic().run()}
        className={`p-2 hover:bg-[rgba(212,175,55,0.2)] transition-colors ${
          editor.isActive('italic') ? 'bg-[rgba(212,175,55,0.3)] text-[#D4AF37]' : 'text-[#A1A1AA]'
        }`}
        title="Italic"
      >
        <Italic className="w-4 h-4" />
      </button>
      <button
        type="button"
        onClick={() => editor.chain().focus().toggleUnderline().run()}
        className={`p-2 hover:bg-[rgba(212,175,55,0.2)] transition-colors ${
          editor.isActive('underline') ? 'bg-[rgba(212,175,55,0.3)] text-[#D4AF37]' : 'text-[#A1A1AA]'
        }`}
        title="Underline"
      >
        <UnderlineIcon className="w-4 h-4" />
      </button>

      <div className="w-px bg-[rgba(212,175,55,0.2)] mx-1" />

      {/* Text Alignment */}
      <button
        type="button"
        onClick={() => editor.chain().focus().setTextAlign('left').run()}
        className={`p-2 hover:bg-[rgba(212,175,55,0.2)] transition-colors ${
          editor.isActive({ textAlign: 'left' }) ? 'bg-[rgba(212,175,55,0.3)] text-[#D4AF37]' : 'text-[#A1A1AA]'
        }`}
        title="Align Left"
      >
        <AlignLeft className="w-4 h-4" />
      </button>
      <button
        type="button"
        onClick={() => editor.chain().focus().setTextAlign('center').run()}
        className={`p-2 hover:bg-[rgba(212,175,55,0.2)] transition-colors ${
          editor.isActive({ textAlign: 'center' }) ? 'bg-[rgba(212,175,55,0.3)] text-[#D4AF37]' : 'text-[#A1A1AA]'
        }`}
        title="Align Center"
      >
        <AlignCenter className="w-4 h-4" />
      </button>
      <button
        type="button"
        onClick={() => editor.chain().focus().setTextAlign('right').run()}
        className={`p-2 hover:bg-[rgba(212,175,55,0.2)] transition-colors ${
          editor.isActive({ textAlign: 'right' }) ? 'bg-[rgba(212,175,55,0.3)] text-[#D4AF37]' : 'text-[#A1A1AA]'
        }`}
        title="Align Right"
      >
        <AlignRight className="w-4 h-4" />
      </button>

      <div className="w-px bg-[rgba(212,175,55,0.2)] mx-1" />

      {/* Lists */}
      <button
        type="button"
        onClick={() => editor.chain().focus().toggleBulletList().run()}
        className={`p-2 hover:bg-[rgba(212,175,55,0.2)] transition-colors ${
          editor.isActive('bulletList') ? 'bg-[rgba(212,175,55,0.3)] text-[#D4AF37]' : 'text-[#A1A1AA]'
        }`}
        title="Bullet List"
      >
        <List className="w-4 h-4" />
      </button>
      <button
        type="button"
        onClick={() => editor.chain().focus().toggleOrderedList().run()}
        className={`p-2 hover:bg-[rgba(212,175,55,0.2)] transition-colors ${
          editor.isActive('orderedList') ? 'bg-[rgba(212,175,55,0.3)] text-[#D4AF37]' : 'text-[#A1A1AA]'
        }`}
        title="Numbered List"
      >
        <ListOrdered className="w-4 h-4" />
      </button>

      <div className="w-px bg-[rgba(212,175,55,0.2)] mx-1" />

      {/* Link */}
      <button
        type="button"
        onClick={setLink}
        className={`p-2 hover:bg-[rgba(212,175,55,0.2)] transition-colors ${
          editor.isActive('link') ? 'bg-[rgba(212,175,55,0.3)] text-[#D4AF37]' : 'text-[#A1A1AA]'
        }`}
        title="Add Link"
      >
        <LinkIcon className="w-4 h-4" />
      </button>

      <div className="w-px bg-[rgba(212,175,55,0.2)] mx-1" />

      {/* Colors */}
      <div className="relative group">
        <button
          type="button"
          className="p-2 hover:bg-[rgba(212,175,55,0.2)] transition-colors text-[#A1A1AA]"
          title="Text Color"
        >
          <Palette className="w-4 h-4" />
        </button>
        <div className="absolute left-0 top-full mt-1 hidden group-hover:flex flex-wrap gap-1 p-2 bg-[#121212] border border-[rgba(212,175,55,0.2)] z-10 w-32">
          {colors.map((color) => (
            <button
              key={color.value}
              type="button"
              onClick={() => editor.chain().focus().setColor(color.value).run()}
              className="w-6 h-6 border border-[rgba(212,175,55,0.3)] hover:scale-110 transition-transform"
              style={{ backgroundColor: color.value }}
              title={color.name}
            />
          ))}
        </div>
      </div>

      {/* Headings */}
      <div className="w-px bg-[rgba(212,175,55,0.2)] mx-1" />
      <select
        onChange={(e) => {
          const level = parseInt(e.target.value);
          if (level === 0) {
            editor.chain().focus().setParagraph().run();
          } else {
            editor.chain().focus().toggleHeading({ level }).run();
          }
        }}
        className="bg-[#121212] border border-[rgba(212,175,55,0.2)] text-[#A1A1AA] text-sm px-2 py-1 focus:outline-none focus:border-[#D4AF37]"
        value={
          editor.isActive('heading', { level: 1 }) ? 1 :
          editor.isActive('heading', { level: 2 }) ? 2 :
          editor.isActive('heading', { level: 3 }) ? 3 : 0
        }
      >
        <option value="0">Paragraph</option>
        <option value="1">Heading 1</option>
        <option value="2">Heading 2</option>
        <option value="3">Heading 3</option>
      </select>
    </div>
  );
};

const RichTextEditor = ({ content, onChange, placeholder = "Start typing..." }) => {
  const editor = useEditor({
    extensions: [
      StarterKit,
      Underline,
      TextStyle,
      Color,
      Link.configure({
        openOnClick: false,
      }),
      TextAlign.configure({
        types: ['heading', 'paragraph'],
      }),
    ],
    content: content || '',
    onUpdate: ({ editor }) => {
      onChange(editor.getHTML());
    },
    editorProps: {
      attributes: {
        class: 'prose prose-invert max-w-none min-h-[200px] p-4 focus:outline-none text-[#F5F5F0]',
      },
    },
  });

  return (
    <div className="border border-[rgba(212,175,55,0.3)] bg-[#121212] overflow-hidden">
      <MenuBar editor={editor} />
      <EditorContent editor={editor} />
      <style>{`
        .ProseMirror {
          min-height: 200px;
          padding: 1rem;
        }
        .ProseMirror p {
          margin: 0.5em 0;
          color: #A1A1AA;
        }
        .ProseMirror h1 {
          font-size: 2em;
          font-weight: bold;
          color: #F5F5F0;
          font-family: 'Playfair Display', serif;
        }
        .ProseMirror h2 {
          font-size: 1.5em;
          font-weight: bold;
          color: #F5F5F0;
          font-family: 'Playfair Display', serif;
        }
        .ProseMirror h3 {
          font-size: 1.25em;
          font-weight: bold;
          color: #D4AF37;
        }
        .ProseMirror ul, .ProseMirror ol {
          padding-left: 1.5em;
          color: #A1A1AA;
        }
        .ProseMirror li {
          margin: 0.25em 0;
        }
        .ProseMirror a {
          color: #D4AF37;
          text-decoration: underline;
        }
        .ProseMirror:focus {
          outline: none;
        }
        .ProseMirror p.is-editor-empty:first-child::before {
          content: attr(data-placeholder);
          float: left;
          color: #52525B;
          pointer-events: none;
          height: 0;
        }
      `}</style>
    </div>
  );
};

export default RichTextEditor;
